/**
 * 本批新增页面的 React Query hooks（目标、打卡、积分、奖品、兑换、统计）。
 *
 * 约定与 `lib/queries.ts` 保持一致：
 *  - 查询键集中在 `queryKeys` 里，写操作成功后精确失效对应的键；
 *  - `retry: 1` —— 接口未就绪时不要疯狂重试；
 *  - 写操作返回 mutation，页面负责把 ApiError 的中文 message 交给 Toast。
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { NodeKind, PointsReason, RedemptionStatus } from '@fht/shared';
import * as checkinsApi from './api/checkins';
import * as commentsApi from './api/comments';
import * as homeworkApi from './api/homework';
import * as nodesApi from './api/nodes';
import * as pointsApi from './api/points';
import * as statsApi from './api/stats';
import type { HabitWritePayload, NodeWritePayload } from './api/types';
import { queryKeys } from './queries';

/** 本批新增的查询键（挂在既有 queryKeys 的命名习惯上）。 */
export const domainKeys = {
  /**
   * 节点树的查询键。
   *
   * `kind` 省略（界面现在的用法）时用 `'all'` 占位 —— **不能**用 `undefined`
   * 或省掉这一段：那样会与历史上"按 kind 拉取"的缓存键撞在一起，
   * 出现"整棵树"与"某个 kind 的子树"互相覆盖。
   */
  nodeTree: (studentId: string, kind?: NodeKind) =>
    ['students', studentId, 'nodes', kind ?? 'all'] as const,
  nodeHabits: (nodeId: string) => ['nodes', nodeId, 'habits'] as const,
  today: (studentId: string) => ['student', studentId, 'today-detail'] as const,
  ledger: (studentId: string, page: number, reason: string) =>
    ['students', studentId, 'ledger', page, reason] as const,
  studentRewards: (studentId: string) => ['students', studentId, 'rewards'] as const,
  redemptions: (studentId: string) => ['students', studentId, 'redemptions'] as const,
  pendingRedemptions: (studentId: string) =>
    ['students', studentId, 'redemptions', 'pending'] as const,
  overview: (studentId: string, range: string) =>
    ['students', studentId, 'stats', 'overview', range] as const,
  timeseries: (studentId: string, metric: string, interval: string) =>
    ['students', studentId, 'stats', 'timeseries', metric, interval] as const,
  statsNodes: (studentId: string, nodeId: string) =>
    ['students', studentId, 'stats', 'nodes', nodeId] as const,
  heatmap: (studentId: string, year: number) =>
    ['students', studentId, 'stats', 'heatmap', year] as const,
  comments: (targetType: string, targetId: string) =>
    ['comments', targetType, targetId] as const,
  /**
   * 某一天的作业单。**按日期分键**：翻到昨天 / 下周一各自一份缓存，
   * 否则"切一天"会先把上一天的内容显示出来再跳变。
   */
  homeworkDay: (studentId: string, date: string) =>
    ['students', studentId, 'homework', date] as const,
  homeworkHistory: (studentId: string, from: string, to: string) =>
    ['students', studentId, 'homework', 'history', from, to] as const,
  /**
   * 「超时」作业队列。
   *
   * ⚠️ 它是 `['students', id, 'homework', …]` 的**子键**，所以作业写操作里那条
   * `invalidateQueries({ queryKey: ['students', id, 'homework'] })` 会连它一起失效
   * —— 这是刻意的：超时队列与作业单读的是同一批 `checkin` 行，
   * 作业项的完成/放弃/验收必须让两者同时刷新，否则会出现
   * "今日标签里已完成、超时标签里还挂着"的错觉。
   */
  homeworkOverdue: (studentId: string) =>
    ['students', studentId, 'homework', 'overdue'] as const,
  /**
   * 「**还有效**（还没到期、但已经不是今天布置的）」作业队列。
   *
   * 与 `homeworkOverdue` 同样是 `['students', id, 'homework', …]` 的**子键**：
   * 作业写操作（开始/完成/放弃/验收）里那条前缀失效会连它一起刷新 ——
   * 这三个视图读的是同一批 `checkin` 行，只失效其中一个必然出现
   * "还有效里已经完成、超时里还挂着"的错觉。
   */
  homeworkOpen: (studentId: string) =>
    ['students', studentId, 'homework', 'open'] as const,
} as const;

// ---------------------------------------------------------------- 目标

/**
 * 节点树（**一棵树**）。
 *
 * `kind` 可选：**不传就是全部节点**（界面现在的唯一用法，见 `ParentNodesPage`）——
 * 后端省略 `kind` 时返回整棵树，历史上 kind='task' 的节点也在其中。
 * 参数保留下来是为了不删掉这个共享工具（也允许个别调用方仍按 kind 拉取）。
 */
export function useNodeTree(studentId: string | null, kind?: NodeKind) {
  return useQuery({
    queryKey: domainKeys.nodeTree(studentId ?? 'none', kind),
    queryFn: () => nodesApi.listNodeTree(studentId as string, kind),
    enabled: Boolean(studentId),
    retry: 1,
  });
}

/**
 * 节点写操作。
 *
 * 状态变更（暂停/恢复/完成/停止/重新开启）与归档各自**独立成 mutation**，
 * 因为它们走的是专用端点而不是 `PATCH /nodes/:id`：暂停/停止要向下传播，
 * 恢复要区分"被父级带下来的"和"用户手动按的"（见 api/nodes.ts 的文件头注释）。
 */
export function useNodeMutations(studentId: string) {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['students', studentId, 'nodes'] });
    // 统计页的进度树与节点树同源，一并失效
    void qc.invalidateQueries({ queryKey: ['students', studentId, 'stats'] });
    // 子目标的状态会影响孩子端的今日打卡清单（只有进行中的才显示）
    void qc.invalidateQueries({ queryKey: domainKeys.today(studentId) });
  };

  return {
    create: useMutation({
      mutationFn: (payload: NodeWritePayload & { kind: NodeKind; parentId: string | null }) =>
        nodesApi.createNode(studentId, payload),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: (vars: { nodeId: string; payload: NodeWritePayload }) =>
        nodesApi.updateNode(vars.nodeId, vars.payload),
      onSuccess: invalidate,
    }),
    /** 状态变更：一个 mutation 覆盖 5 个动作，页面按动作取用。 */
    setStatus: useMutation({
      mutationFn: (vars: { nodeId: string; action: NodeStatusAction }) =>
        nodesApi.changeNodeStatus(vars.nodeId, vars.action),
      onSuccess: invalidate,
    }),
    setArchived: useMutation({
      mutationFn: (vars: { nodeId: string; archived: boolean }) =>
        vars.archived ? nodesApi.archiveNode(vars.nodeId) : nodesApi.unarchiveNode(vars.nodeId),
      onSuccess: invalidate,
    }),
    remove: useMutation({ mutationFn: nodesApi.deleteNode, onSuccess: invalidate }),
  };
}

/** 五个状态动作，与后端 `POST /nodes/:id/<action>` 一一对应。 */
export type NodeStatusAction = 'pause' | 'resume' | 'complete' | 'stop' | 'reopen';

// ---------------------------------------------------------------- 打卡任务

export function useNodeHabits(nodeId: string | null) {
  return useQuery({
    queryKey: domainKeys.nodeHabits(nodeId ?? 'none'),
    queryFn: () => nodesApi.listHabits(nodeId as string),
    enabled: Boolean(nodeId),
    retry: 1,
  });
}

export function useHabitMutations(studentId: string, nodeId: string | null) {
  const qc = useQueryClient();
  const invalidate = () => {
    if (nodeId) void qc.invalidateQueries({ queryKey: domainKeys.nodeHabits(nodeId) });
    void qc.invalidateQueries({ queryKey: ['students', studentId, 'nodes'] });
    void qc.invalidateQueries({ queryKey: domainKeys.today(studentId) });
  };

  return {
    create: useMutation({
      mutationFn: (vars: { nodeId: string; payload: HabitWritePayload }) =>
        nodesApi.createHabit(vars.nodeId, vars.payload),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: (vars: { habitId: string; payload: Partial<HabitWritePayload> }) =>
        nodesApi.updateHabit(vars.habitId, vars.payload),
      onSuccess: invalidate,
    }),
    remove: useMutation({ mutationFn: nodesApi.deleteHabit, onSuccess: invalidate }),
  };
}

// ---------------------------------------------------------------- 打卡与计时

export function useToday(studentId: string | null) {
  return useQuery({
    queryKey: domainKeys.today(studentId ?? 'none'),
    queryFn: () => checkinsApi.studentToday(studentId as string),
    enabled: Boolean(studentId),
    retry: 1,
    /**
     * ⚠️ 计时中的秒数（`actualSeconds`）是**服务端当场算的实时值**，不是可缓存的快照
     * （`checkin.actual_seconds` 那一列在 pause/heartbeat 时都不写回）。
     * 全局 `staleTime` 是 15 秒 —— 从别的页面切回来时若命中缓存，面板会拿着
     * "刚开始时 ≈ 0" 的旧值重画，看起来就是"计时从 0 开始"（2026-10-29 用户报的）。
     * 所以这一条查询**每次挂载都重新取**。
     */
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export interface TimerMutationVars {
  checkinId: string;
  /** 一次用户意图一个幂等键，重试时复用（见 client.newIdempotencyKey）。 */
  idempotencyKey: string;
  estimatedSeconds?: number;
  valueDone?: number | null;
  note?: string;
  reason?: string;
}

/**
 * 打卡计时类写操作（`start` / `pause` / `resume` / `finish` / `skip` / `makeup` / `undo`）。
 *
 * `extraInvalidate` 是给**作业页**留的扩展点：作业项走的是**同一批**
 * `/checkins/:id/*` 端点（服务端计时只有一套，刻意不另写），
 * 但作业节点被 `/student/today` 排除，所以作业页自己的缓存必须由调用方补失效。
 * 默认不传 = 既有调用方（今日打卡页）行为**一字未改**。
 */
export function useCheckinTimerMutations(studentId: string, extraInvalidate?: () => void) {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: domainKeys.today(studentId) });
    void qc.invalidateQueries({ queryKey: domainKeys.ledger(studentId, 1, 'all') });
    void qc.invalidateQueries({ queryKey: ['students', studentId, 'points'] });
    void qc.invalidateQueries({ queryKey: domainKeys.overview(studentId, '') });
    void qc.invalidateQueries({ queryKey: ['students', studentId, 'stats'] });
    extraInvalidate?.();
  };

  return {
    start: useMutation({
      mutationFn: (vars: TimerMutationVars) =>
        checkinsApi.startCheckin(vars.checkinId, vars.estimatedSeconds ?? 0, {
          idempotencyKey: vars.idempotencyKey,
        }),
      onSuccess: invalidate,
    }),
    pause: useMutation({
      mutationFn: (vars: TimerMutationVars) =>
        checkinsApi.pauseCheckin(vars.checkinId, { idempotencyKey: vars.idempotencyKey }),
      onSuccess: invalidate,
    }),
    resume: useMutation({
      mutationFn: (vars: TimerMutationVars) =>
        checkinsApi.resumeCheckin(vars.checkinId, { idempotencyKey: vars.idempotencyKey }),
      onSuccess: invalidate,
    }),
    finish: useMutation({
      mutationFn: (vars: TimerMutationVars) =>
        checkinsApi.finishCheckin(
          vars.checkinId,
          { valueDone: vars.valueDone ?? null, note: vars.note ?? '' },
          { idempotencyKey: vars.idempotencyKey },
        ),
      onSuccess: invalidate,
    }),
    skip: useMutation({
      mutationFn: (vars: TimerMutationVars) =>
        checkinsApi.skipCheckin(vars.checkinId, vars.reason ?? '', {
          idempotencyKey: vars.idempotencyKey,
        }),
      onSuccess: invalidate,
    }),
    makeup: useMutation({
      mutationFn: (vars: TimerMutationVars) =>
        checkinsApi.makeupCheckin(
          vars.checkinId,
          { valueDone: vars.valueDone ?? null, note: vars.note ?? '' },
          { idempotencyKey: vars.idempotencyKey },
        ),
      onSuccess: invalidate,
    }),
    undo: useMutation({
      mutationFn: (vars: TimerMutationVars) =>
        checkinsApi.undoCheckin(vars.checkinId, { idempotencyKey: vars.idempotencyKey }),
      onSuccess: invalidate,
    }),
    /**
     * 「撤销放弃」（`skipped → pending`）—— 2026-10-17 新增，孩子误点「放弃」的回程。
     *
     * 与 `undo` 是**两件事**（别合并）：`undo` 是家长撤销"已完成"的记录，
     * 这条是把"已放弃"退回未完成，孩子本人也能用（限自己今天、非作业项）。
     * 失效范围与其它打卡写操作完全一致 —— 放弃/恢复都会改「今日清单」的状态、
     * 可能冲正积分（`ledger` / `points`），也可能影响作业页的队列，
     * 所以调用方（今日打卡页 / 作业页）都通过同一个 `invalidate` 收口。
     */
    restore: useMutation({
      mutationFn: (vars: TimerMutationVars) =>
        checkinsApi.restoreCheckin(
          vars.checkinId,
          { reason: vars.reason ?? '' },
          { idempotencyKey: vars.idempotencyKey },
        ),
      onSuccess: invalidate,
    }),
  };
}

// ---------------------------------------------------------------- 积分 / 奖品 / 兑换

export function usePointsSummary(studentId: string | null) {
  return useQuery({
    queryKey: ['students', studentId ?? 'none', 'points-summary'],
    queryFn: () => pointsApi.getPointsSummary(studentId as string),
    enabled: Boolean(studentId),
    retry: 1,
  });
}

export function useLedger(studentId: string | null, page: number, reason: PointsReason | 'all') {
  return useQuery({
    queryKey: domainKeys.ledger(studentId ?? 'none', page, reason),
    queryFn: () =>
      pointsApi.getLedger(studentId as string, {
        page,
        pageSize: 20,
        ...(reason === 'all' ? {} : { reason }),
      }),
    enabled: Boolean(studentId),
    retry: 1,
  });
}

export function useStudentRewardList(studentId: string | null) {
  return useQuery({
    queryKey: domainKeys.studentRewards(studentId ?? 'none'),
    queryFn: () => pointsApi.listStudentRewards(studentId as string),
    enabled: Boolean(studentId),
    retry: 1,
  });
}

/**
 * 家长的**一整套**奖品目录（`GET /rewards`）。
 *
 * 奖品是家长作用域的：共用奖品在这里只出现一条，`students` 带上每个孩子的余额，
 * 界面据此回答"哪个孩子换得起"。页面不再需要先选一个孩子。
 */
export function useMyRewardCatalog() {
  return useQuery({
    queryKey: queryKeys.rewards,
    queryFn: () => pointsApi.listMyRewardCatalog(),
    retry: 1,
  });
}

/** 奖品的增删改。`studentId` 只用于失效"某个孩子的目录"缓存（家长目录一律失效）。 */
export function useRewardMutations(studentId: string | null) {
  const qc = useQueryClient();
  const invalidate = () => {
    if (studentId) void qc.invalidateQueries({ queryKey: domainKeys.studentRewards(studentId) });
    void qc.invalidateQueries({ queryKey: queryKeys.rewards });
  };

  return {
    create: useMutation({ mutationFn: pointsApi.createReward, onSuccess: invalidate }),
    update: useMutation({
      mutationFn: (vars: { id: string; payload: Partial<pointsApi.RewardWritePayload> }) =>
        pointsApi.updateReward(vars.id, vars.payload),
      onSuccess: invalidate,
    }),
    remove: useMutation({ mutationFn: pointsApi.deleteReward, onSuccess: invalidate }),
  };
}

export function useRedemptions(studentId: string | null, status?: RedemptionStatus) {
  return useQuery({
    // 按 status 分键，避免"筛选到待审批"的结果覆盖掉"全部"那一份缓存
    queryKey: [
      ...domainKeys.redemptions(studentId ?? 'none'),
      status ?? 'all',
    ] as const,
    queryFn: () => pointsApi.listRedemptions(studentId as string, status),
    enabled: Boolean(studentId),
    retry: 1,
  });
}

export function useRedemptionMutations(studentId: string) {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: domainKeys.redemptions(studentId) });
    void qc.invalidateQueries({ queryKey: domainKeys.pendingRedemptions(studentId) });
    void qc.invalidateQueries({ queryKey: domainKeys.studentRewards(studentId) });
    void qc.invalidateQueries({ queryKey: ['students', studentId, 'points'] });
    void qc.invalidateQueries({ queryKey: domainKeys.ledger(studentId, 1, 'all') });
  };

  return {
    request: useMutation({
      mutationFn: (vars: { studentId: string; rewardId: string; note: string; idempotencyKey: string }) =>
        pointsApi.requestRedemption(
          vars.studentId,
          { rewardId: vars.rewardId, note: vars.note },
          vars.idempotencyKey,
        ),
      onSuccess: invalidate,
    }),
    decide: useMutation({
      mutationFn: (vars: {
        id: string;
        decision: pointsApi.RedemptionDecision;
        note: string;
        idempotencyKey: string;
      }) =>
        pointsApi.decideRedemption(vars.id, vars.decision, vars.note, vars.idempotencyKey),
      onSuccess: invalidate,
    }),
  };
}

export function usePointsMutations(studentId: string) {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['students', studentId, 'points'] });
    void qc.invalidateQueries({ queryKey: domainKeys.ledger(studentId, 1, 'all') });
    void qc.invalidateQueries({ queryKey: ['students', studentId, 'points-summary'] });
  };

  return {
    adjust: useMutation({
      mutationFn: (vars: {
        delta: number;
        reason: Extract<PointsReason, 'bonus' | 'penalty' | 'adjust'>;
        note: string;
        idempotencyKey: string;
      }) =>
        pointsApi.adjustPoints(
          studentId,
          { delta: vars.delta, reason: vars.reason, note: vars.note },
          vars.idempotencyKey,
        ),
      onSuccess: invalidate,
    }),
    reverse: useMutation({
      mutationFn: (vars: { ledgerId: string; note: string; idempotencyKey: string }) =>
        pointsApi.reverseLedgerEntry(vars.ledgerId, vars.note, vars.idempotencyKey),
      onSuccess: invalidate,
    }),
  };
}

// ---------------------------------------------------------------- 统计

export function useStatsOverview(studentId: string | null, range = '') {
  return useQuery({
    queryKey: domainKeys.overview(studentId ?? 'none', range),
    queryFn: () => statsApi.getOverview(studentId as string),
    enabled: Boolean(studentId),
    retry: 1,
  });
}

export function useTimeseries(
  studentId: string | null,
  metric: statsApi.TimeseriesMetric,
  interval: statsApi.TimeseriesInterval,
) {
  return useQuery({
    queryKey: domainKeys.timeseries(studentId ?? 'none', metric, interval),
    queryFn: () => statsApi.getTimeseries(studentId as string, metric, interval),
    enabled: Boolean(studentId),
    retry: 1,
  });
}

export function useStatsNodes(studentId: string | null, nodeId = '') {
  return useQuery({
    queryKey: domainKeys.statsNodes(studentId ?? 'none', nodeId),
    queryFn: () => statsApi.getStatsNodes(studentId as string, nodeId || undefined),
    enabled: Boolean(studentId),
    retry: 1,
  });
}

export function useHeatmap(studentId: string | null, year: number) {
  return useQuery({
    queryKey: domainKeys.heatmap(studentId ?? 'none', year),
    queryFn: () => statsApi.getHeatmap(studentId as string, year),
    enabled: Boolean(studentId),
    retry: 1,
  });
}

// ---------------------------------------------------------------- 学校作业

/**
 * 某一天的作业单（科目 + 作业项 + 打卡状态 + 完成聚合）。
 *
 * ## `date` 的三种取值（⚠️ 这里踩过一次真实的生产事故）
 *
 *  - `'YYYY-MM-DD'`：看那一天（家长端翻日期用）；
 *  - `null`：**让服务端按家庭时区决定"今天"是哪一天**（学生端用）——
 *    接口契约就是"省略 `date` = 家庭时区下的今天"，所以这条请求照发；
 *  - 传 `studentId` 为 null：不发请求（学生端要先把 auth_user.id 解析成 student.id）。
 *
 * 曾经的写法是 `enabled: Boolean(studentId) && Boolean(date)`，于是学生端
 * `useHomeworkDay(id, null)` **一次请求都不发**：`day` 永远是 undefined，
 * 页面既不显示骨架也不报错，直接落到"今天没有布置作业"的空状态 ——
 * 这就是用户报的"孩子端作业页不显示各科的作业内容"（服务端日志里
 * 学生端连一条 `GET /students/:id/homework` 都没有）。
 * 用 `date=null` 表达"还没确定日期"是**错的**：那件事由 `studentId` 为空表达。
 */
export function useHomeworkDay(studentId: string | null, date: string | null) {
  return useQuery({
    queryKey: domainKeys.homeworkDay(studentId ?? 'none', date ?? 'server-today'),
    queryFn: () => homeworkApi.getHomeworkDay(studentId as string, date ?? undefined),
    enabled: Boolean(studentId),
    retry: 1,
    /**
     * 与 `useToday` 同一条理由（2026-10-29 用户报的）：作业页计时中切走再切回，
     * 面板会拿缓存里"刚开始时 ≈ 0"的 `actualSeconds` 重画 —— 表现为"计时从 0 重新开始"。
     * 计时秒数是服务端实时算的派生值，不该被 `staleTime` 当成可复用快照。
     */
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

/**
 * 过往作业单列表（给"查过往某天"，区间缺省最近 30 天）。
 *
 * ⚠️ 响应里现在带**每天的科目名**（服务端只读字段），录入表单的「科目」下拉
 * 就是拿它当候选（见 `lib/homeworkSubjects.ts` 的 `subjectSuggestionsOf()`）。
 * 那两个消费方（录入表单与「查过往的作业」）用的是同一个 hook 与同一个
 * queryKey —— 所以同一天无论谁先请求，都不会多打一次接口。
 *
 * `enabled` 只给"暂时用不到候选"的调用方（例如只想让用户手输）用，
 * 默认 `true` 与既有行为一字不差。
 */
export function useHomeworkHistory(
  studentId: string | null,
  range: { from?: string; to?: string } = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: domainKeys.homeworkHistory(studentId ?? 'none', range.from ?? '', range.to ?? ''),
    queryFn: () => homeworkApi.listHomeworkHistory(studentId as string, range),
    enabled: Boolean(studentId) && options.enabled !== false,
    retry: 1,
  });
}

/**
 * 「超时」作业队列（`GET /students/:id/homework/overdue`）。
 *
 * 排序（`dueAt` 倒序）与"超时"判据都在服务端，这里只取回来显示 ——
 * 前端**不重排**，否则"谁说了算"就说不清了（见 `api/homework.ts` 的注释）。
 *
 * `studentId` 为 null 时不发请求（学生端要先把 auth_user.id 解析成 student.id）。
 */
export function useHomeworkOverdue(studentId: string | null) {
  return useQuery({
    queryKey: domainKeys.homeworkOverdue(studentId ?? 'none'),
    queryFn: () => homeworkApi.listHomeworkOverdue(studentId as string),
    enabled: Boolean(studentId),
    retry: 1,
  });
}

/**
 * 「**还有效**」作业队列（`GET /students/:id/homework/open`）。
 *
 * 这一条补的是用户实测报的缝隙：周五布置、周日截止的未完成作业，
 * 周六在「今天的作业」（按布置日筛）与「超时」（按 `due_at < now` 筛）里都看不见。
 * 排序（`dueAt` **正序**，越早到期越靠前）与判据都在服务端，前端**不重排**。
 *
 * `studentId` 为 null 时不发请求（学生端要先把 auth_user.id 解析成 student.id）。
 */
export function useHomeworkOpen(studentId: string | null) {
  return useQuery({
    queryKey: domainKeys.homeworkOpen(studentId ?? 'none'),
    queryFn: () => homeworkApi.listHomeworkOpen(studentId as string),
    enabled: Boolean(studentId),
    retry: 1,
  });
}

/**
 * 学生端作业页的**计时类写操作**。
 *
 * ⚠️ 这里**没有另写一套计时器**：它原样复用打卡的 `useCheckinTimerMutations`
 * （start/pause/resume/finish/skip/makeup），服务端那套防篡改计时、
 * 心跳自动暂停、幂等键全部照旧生效。
 *
 * 传进去的 `extraInvalidate` 只补一件事：**作业页自己的缓存**。
 * 为什么不靠打卡那套失效逻辑：它失效的是 `/student/today` 与积分，
 * 而作业节点被 `/student/today` **无条件排除**
 * （见 `checkins.service.ts` 的 `ne(nodes.kind, 'homework')`），
 * 所以今日清单失效对作业页毫无作用 —— 不补这一步，孩子点完「完成」，
 * 卡片会一直停在旧状态，"超时"队列也不会消失。
 */
export function useHomeworkCheckinMutations(studentId: string | null) {
  const qc = useQueryClient();
  /**
   * ⚠️ 失效的是 `['students', id, 'homework']` 这个**前缀**：
   * 作业单（按日期分键）与「超时」队列（同一前缀的子键）会一起失效。
   * 两者读的是同一批 `checkin` 行，分开失效必然出现
   * "今日标签已完成、超时标签还挂着"。
   */
  const invalidateHomework = () => {
    if (!studentId) return;
    void qc.invalidateQueries({ queryKey: ['students', studentId, 'homework'] });
  };

  return useCheckinTimerMutations(studentId ?? 'none', invalidateHomework);
}

/**
 * 作业写操作成功之后要做的**数据层**动作：先把这一次的响应播种进缓存，再失效。
 *
 * ## ⚠️ 为什么必须在失效**之前**播种（2026-10-14 用户实测的那个 bug）
 *
 * 用户原话：
 *
 * > 在录入作业界面，点「复制上次结构」后，**在所在面并没有显示复制过来的作业**。
 * > 但点了「退出录入」之后，又显示了这些作业（服务端确实写成功了）。
 *
 * 根因不在"复制"，而在**写完之后页面读到的是哪一份 `day`**。
 * `invalidateQueries()` 触发的重取是**异步**的，而且这里的 promise 被 `void` 掉了，
 * 所以 `mutateAsync()` 会在"新数据还没回来"时就已经 resolve。而家长端页面那一侧是：
 *
 *     写成功 → `setSyncToken(+1)` → 在下一次渲染里用 `day` 重建草稿
 *
 * 如果那一刻缓存里还是**写之前**的那一份：
 *   1. 重建出来的草稿是旧的 —— 用户在录入态看到的表单"毫无反应"（他报的就是这一幕）；
 *   2. 页面记下的同步键（`syncedKeyRef`）却已经被消费掉，等真正的新数据回来时
 *      反而被当成"后台刷新"忽略 → **永远**不会再重建（"退出录入"看到的是另一条渲染路径，
 *      它直接读 `day`，所以反而显示正常 —— 这三条现象拼起来就是用户描述的完整形状）。
 *
 * 实测证据（机器可复现，见 `apps/web/src/lib/__tests__/homeworkWriteCache.test.ts`）：
 * 只失效、不播种时，`mutateAsync` resolve 那一刻读到的是**旧的** `exists: false`。
 *
 * 修法就是把"服务端返回的这一天"**立刻**写进缓存 —— 它本来就是写完之后的真实状态，
 * 没有比它更新的数据了。之后照旧失效：重取回来只会把它再确认一遍。
 * 这条对**保存 / 复制 / 清空**三条路径同样成立（它们都是"写完就必须以服务端结果为准"），
 * 所以它放在写操作共用的 onSuccess 里，而不是某个页面里。
 *
 * ⚠️ 播种用的是**响应里的 `date`**，不是调用方传进来的那个日期：
 * `PUT`/`copy` 省略日期时由服务端解析成家庭时区下的今天，
 * 用本地时间算出来的"今天"在跨时区/跨午夜时会对不上。
 */
export function homeworkWriteOnSuccess(
  qc: QueryClient,
  studentId: string,
  result: homeworkApi.HomeworkDayResult,
): void {
  /** ① 播种：写完之后这一天的真实样子 —— 页面紧接着那一次渲染读到的就是它。 */
  qc.setQueryData(domainKeys.homeworkDay(studentId, result.date), result);

  /** ② 失效：范围与既有行为一字不差（作业当天 / 历史 + 孩子端今日 + 统计）。 */
  void qc.invalidateQueries({ queryKey: ['students', studentId, 'homework'] });
  void qc.invalidateQueries({ queryKey: domainKeys.today(studentId) });
  // 统计口径读的是 checkin 事实表，作业项同样计入，所以统计页也要刷新
  void qc.invalidateQueries({ queryKey: ['students', studentId, 'stats'] });
  /**
   * ⚠️ 今天这一天的作业日**必须按查询键精确失效**，不能只靠上面那条前缀。
   *
   * 为什么这里要单独再来一次：写完页面会 `setSyncToken(+1)`，
   * 用**服务端返回的结果**重建草稿（"刚写完就以服务端为准"）。
   * 如果这一天恰好有一份**已过期但还没被重新拉取**的缓存，
   * 重建出来的草稿就会是"上一次读到的样子" —— 而"草稿就是这一天该长什么样"
   * 正是本模块最危险的一条不变式（草稿少了什么，保存就会把它从计划里移除）。
   * 前缀失效照理说已经覆盖它，但这条精确失效**成本为零**，
   * 换来的是"重建草稿时读到的一定是刚写进去的那一份"。
   */
  void qc.invalidateQueries({ queryKey: domainKeys.homeworkDay(studentId, result.date) });
}

/**
 * 作业写操作（整单保存 / 复制上次的结构）。
 *
 * ⚠️ 失效范围刻意**不含** `['students', id, 'nodes']`：作业节点被后端
 * 隔离在自己的页面里（`GET /students/:id/nodes` 无条件排除 `kind='homework'`），
 * 所以作业的增删改不会让目标树的缓存过期。反过来这里要失效的是
 * **作业当天 / 历史**，以及孩子端的今日清单（作业项的打卡记录就在里面）。
 *
 * ⚠️ 成功回调**只走** `homeworkWriteOnSuccess()`：它把"先播种、再失效"的顺序
 * （以及那个顺序为什么不能反）集中在一处，见它的注释 ——
 * 顺序反了就是用户实测的"点复制后表单不刷新"。
 */
export function useHomeworkMutations(studentId: string) {
  const qc = useQueryClient();
  const writeDone = (result: homeworkApi.HomeworkDayResult) =>
    homeworkWriteOnSuccess(qc, studentId, result);

  return {
    save: useMutation({
      mutationFn: (vars: { date: string; input: homeworkApi.SaveHomeworkInput }) =>
        homeworkApi.saveHomework(studentId, vars.date, vars.input),
      onSuccess: writeDone,
    }),
    copy: useMutation({
      mutationFn: (vars: { date: string; fromDate?: string }) =>
        homeworkApi.copyHomework(studentId, vars),
      onSuccess: writeDone,
    }),
  };
}

// ---------------------------------------------------------------- 作业验收（仅家长）

/**
 * 家长验收：通过（`submitted → done`，此刻计分）/ 打回（`submitted → pending`，不计分）。
 *
 * ⛔ 门禁在服务端是 `canApproveCheckin`（**仅家长 owner/editor**），
 * 学生调用一律 403。因此这两个 mutation **只在家长端页面使用** ——
 * 学生端界面连按钮都不渲染（不靠"点了会失败"来保护）。
 *
 * ## 验收后要失效哪些缓存（一个都不能少）
 *
 * 一次验收同时改变四类数据的读数，缺任何一个都会让家长看到自相矛盾的页面：
 *
 *  1. `['students', id, 'homework']` —— **作业日**（那一项要从 `submitted` 变成
 *     `done` / 退回 `pending`）**与「超时」队列**（同一前缀的子键；
 *     通过后它立刻退出超时队列）。前缀失效一次覆盖两者。
 *  2. `domainKeys.today(id)` —— 孩子端今日清单（家长切过去看时要是新的）。
 *  3. `['students', id, 'points']` + 积分账本 —— **通过才计分**，
 *     余额与流水都会变；打回不变，但一并失效是安全的（宁多刷一次，不留旧数）。
 *  4. `['students', id, 'stats']` —— 统计口径读的就是 `checkin` 事实表。
 *
 * 刻意**不动** `['students', id, 'nodes']`：作业节点被后端隔离在自己的页面里
 * （`GET /students/:id/nodes` 无条件排除 `kind='homework'`），验收不会改变目标树。
 *
 * 幂等说明：`approve` 对已通过的行是幂等的（服务端同一幂等键，不会重复加分），
 * 所以家长连点两次是安全的；`reject` **不幂等**，第二次会被后端以 409 明确拒绝
 * —— 页面收到 409 时把错误文案原样 Toast，并失效一次缓存让界面回到真实状态。
 */
export function useHomeworkApproval(studentId: string) {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['students', studentId, 'homework'] });
    void qc.invalidateQueries({ queryKey: domainKeys.today(studentId) });
    void qc.invalidateQueries({ queryKey: ['students', studentId, 'points'] });
    void qc.invalidateQueries({ queryKey: domainKeys.ledger(studentId, 1, 'all') });
    void qc.invalidateQueries({ queryKey: ['students', studentId, 'stats'] });
  };
  /** 409（打回时这条已经不是 `submitted`）也要刷新：界面停在旧状态最误导人。 */
  const invalidateOnSettled = () => invalidate();

  return {
    approve: useMutation({
      mutationFn: (vars: { checkinId: string; idempotencyKey: string }) =>
        checkinsApi.approveCheckin(vars.checkinId, { idempotencyKey: vars.idempotencyKey }),
      onSuccess: invalidateOnSettled,
      onError: invalidateOnSettled,
    }),
    reject: useMutation({
      mutationFn: (vars: { checkinId: string; reason: string; idempotencyKey: string }) =>
        checkinsApi.rejectCheckin(vars.checkinId, vars.reason, {
          idempotencyKey: vars.idempotencyKey,
        }),
      onSuccess: invalidateOnSettled,
      onError: invalidateOnSettled,
    }),
  };
}

// ---------------------------------------------------------------- 点评

export function useComments(
  targetType: 'checkin' | 'node' | 'redemption' | 'period',
  targetId: string | null,
  /** `period` 类型必传（后端无法从 targetId 反查学生，缺它返回 400）。 */
  studentId?: string,
) {
  return useQuery({
    queryKey: domainKeys.comments(targetType, targetId ?? 'none'),
    queryFn: () => commentsApi.listComments(targetType, targetId as string, studentId),
    enabled: Boolean(targetId),
    retry: 1,
  });
}

export function useCreateComment(
  targetType: 'checkin' | 'node' | 'redemption' | 'period',
  targetId: string | null,
  /** `period` 类型必传（同 `useComments`）。 */
  studentId?: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { content: string; mood: commentsApi.CommentMood | null }) =>
      commentsApi.createComment({
        targetType,
        targetId: targetId as string,
        content: vars.content,
        mood: vars.mood,
        ...(studentId ? { studentId } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: domainKeys.comments(targetType, targetId ?? 'none') });
    },
  });
}

export { statsApi, pointsApi, nodesApi, checkinsApi, commentsApi, homeworkApi };
// 统计页面的枚举类型也一并转出，页面不必再从具体的 api 模块深入取
export type { TimeseriesInterval, TimeseriesMetric } from './api/stats';
